<template>
  <div id="trialcontainer" class="max-w-[700px] w-full">
    <div v-if="campaign.image_url" class="rounded-lg mb-2">
      <img
        :src="campaign.image_url"
        :alt="campaign.name"
        class="w-auto h-full object-cover"
      />
    </div>
    <h1 v-else class="text-text-primary text-[36px] font-bold pb-3">
      {{ $t('upgrade.trial.use_your_trial') }}
    </h1>
    <h2
      v-if="campaign.description"
      class="text-text-secondary text-center pb-4"
    >
      {{ campaign.description }}
    </h2>
    <div :class="`bg-gray-${900} rounded-lg shadow-lg`">
      <div
        class="grid grid-cols-1 lg:grid-cols-2 gap-4 divide-x divide-gray-900"
      >
        <div class="p-6 col-span-1 flex flex-col gap-8">
          <div class="flex items-center gap-2">
            <h2 class="text-text-primary text-lg font-bold flex-1">
              {{ tierLabel }}
            </h2>
            <div
              v-if="!isPlayerTier"
              class="text-primary-600 bg-primary-600/10 rounded-lg px-2 py-1 flex text-xs items-center justify-center"
            >
              {{ $t('upgrade.most_popular') }}
            </div>
          </div>
          <div class="flex flex-col gap-3">
            <p class="text-text-secondary text-sm">{{ $t('upgrade.trial.no_credit_card') }}</p>
            <p class="text-text-primary text-2xl font-bold">
              {{ $t('upgrade.trial.free_for_days', { n: campaign.duration_in_days || 7 }) }}
            </p>
            <p class="text-text-secondary text-sm">
              {{ $t('upgrade.trial.regular_price') }}
              <span class="line-through">{{ $t('upgrade.price_per_month', { price: tier?.price }) }}</span>
            </p>
          </div>
        </div>
        <div class="p-6 col-span-1 flex flex-col gap-4">
          <h3 class="text-text-primary text-sm font-bold">{{ $t('upgrade.trial.whats_included') }}</h3>
          <ul class="flex flex-col gap-4 pb-11 flex-1">
            <li
              v-for="feature in tier?.features"
              :key="feature"
              class="gap-2 flex items-center"
            >
              <CheckIcon class="h-5 w-5 text-primary-600 shrink-0" />
              <span class="text-text-secondary text-sm">{{ feature }}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
    <button
      class="bg-primary-600 text-white px-4 py-2 rounded-md w-full flex items-center justify-center gap-1 mt-3"
      :class="[expired && 'disabled:bg-gray-800 cursor-not-allowed', submitting && 'disabled:bg-primary-600/60']"
      :disabled="submitting || expired"
      @click="$emit('submit')"
    >
      <h1
        class="text-white text-sm font-medium"
        :class="expired && 'text-gray-400'"
      >{{ expired ? expiredText : ctaText }}</h1>
      <img
        v-if="!expired"
        class="w-4 ml-1 brightness-150"
        src="../assets/icons/rightarrow.svg"
        alt=""
      />
    </button>
    <h1
      v-if="showSecondaryAction"
      class="text-text-secondary text-sm underline cursor-pointer text-center mt-3"
      @click="$emit('secondaryAction')"
    >
      {{ secondaryActionText }}
    </h1>
  </div>
</template>

<script setup>
import { CheckIcon } from '@heroicons/vue/20/solid';
import { computed } from 'vue';
// Global t: prop default factories run before setup, so useI18n is not available there.
import { t } from '../i18n';

defineEmits(['submit', 'secondaryAction']);

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
  tierLabel: {
    type: String,
    required: true,
  },
  isPlayerTier: {
    type: Boolean,
    required: true,
  },
  tier: {
    type: Object,
    required: true,
  },
  submitting: {
    type: Boolean,
    default: false,
  },
  ctaText: {
    type: String,
    default: () => t('upgrade.trial.try_for_free'),
  },
  showSecondaryAction: {
    type: Boolean,
    default: false,
  },
  secondaryActionText: {
    type: String,
    default: () => t('upgrade.trial.subscribe_instead'),
  },
});

const expired = computed(() => {
  if (!props.campaign.end_date) return false;
  const now = new Date();
  const expirationDate = new Date(props.campaign.end_date);
  return now > expirationDate;
});

const expiredText = computed(() => {
  if (!props.campaign.end_date) return t('upgrade.trial.try_for_free');
  const expirationDate = new Date(props.campaign.end_date);
  return t('upgrade.trial.expired_on', { date: expirationDate.toLocaleDateString() });
});

</script>
